import React, { useState, useCallback, useRef } from "react";
import "../../../Styles/Seller/SellerLogin.css";
import { connect } from "react-redux";
import { showError, showSuccess } from "../../../utils/Toast";
import Modal from "../../Modal/Modal";
import sellerloginimage from "../../../assests/seller-img3.png";
import mainlogo from "../../../assests/elumiya.png";
import sellerApi from "../../../utils/sellerApi";
import { useSellerAuth } from "../../../Common/SellerAuthContext";
import { useNavigate } from "react-router-dom";

const RIGHT_IMAGE = sellerloginimage;
const EMAIL_REGEX = /\S+@\S+\.\S+/;

function SellerLogin() {
  const navigate = useNavigate();
  const { loginSeller } = useSellerAuth();
  // Use refs to track input values without causing re-renders
  const loginEmailRef = useRef("");
  const passwordRef = useRef("");
  const emailRef = useRef("");
  const otpRef = useRef("");
  const newPasswordRef = useRef("");
  
  // Only use state for UI updates (not for every keystroke)
  const [, forceUpdate] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalEmail, setModalEmail] = useState("");
  const [modalOtp, setModalOtp] = useState("");

  // Force re-render for UI updates
  const reRender = useCallback(() => forceUpdate({}), []);

  // Input handlers that don't cause state updates on every keystroke
  const handleLoginEmailChange = useCallback((e) => {
    loginEmailRef.current = e.target.value;
    reRender();
  }, [reRender]);

  const handlePasswordChange = useCallback((e) => {
    passwordRef.current = e.target.value;
    reRender();
  }, [reRender]);

  const handleModalEmailChange = useCallback((e) => {
    emailRef.current = e.target.value;
    setModalEmail(e.target.value);
  }, []);

  const handleOtpChange = useCallback((e) => {
    otpRef.current = e.target.value;
    setModalOtp(e.target.value);
  }, []);

  const handleNewPasswordChange = useCallback((e) => {
    newPasswordRef.current = e.target.value;
  }, []);

  const getErrorMessage = (err, fallbackMessage) => {
    return err?.response?.data?.message || err?.message || fallbackMessage;
  };

  const resetForgotPasswordFlow = useCallback(() => {
    setShowForgotModal(false);
    setStep(1);
    emailRef.current = "";
    otpRef.current = "";
    newPasswordRef.current = "";
    setModalEmail("");
    setModalOtp("");
    setLoading(false);
  }, []);

  const openForgotPasswordModal = useCallback((e) => {
    e.preventDefault();
    const currentEmail = loginEmailRef.current;
    setShowForgotModal(true);
    setStep(1);
    setLoading(false);
    emailRef.current = currentEmail;
    setModalEmail(currentEmail);
    otpRef.current = "";
    setModalOtp("");
    newPasswordRef.current = "";
  }, []);

  const handleForgotPassword = useCallback(async () => {
    const trimmedEmail = (emailRef.current || modalEmail).trim();

    if (!trimmedEmail) {
      showError("Please enter your email address");
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      showError("Please enter a valid email address");
      return;
    }

    try {
      setLoading(true);
      const response = await sellerApi.post("forgotPassword", {
        email: trimmedEmail,
      });
      const responseData = response?.data;

      if (responseData?.success === false) {
        showError(responseData?.message || "Failed to send OTP");
        return;
      }

      showSuccess(responseData?.message || "OTP sent successfully");
      emailRef.current = trimmedEmail;
      setModalEmail(trimmedEmail);
      setStep(2);
    } catch (err) {
      showError(getErrorMessage(err, "Failed to send OTP"));
    } finally {
      setLoading(false);
    }
  }, [modalEmail]);

  const handleVerifyOtp = useCallback(async () => {
    const trimmedEmail = (emailRef.current || modalEmail).trim();
    const trimmedOtp = (otpRef.current || modalOtp).trim();

    if (!trimmedEmail) {
      showError("Please enter your email address");
      return;
    }

    if (!trimmedOtp) {
      showError("Please enter the OTP");
      return;
    }

    try {
      setLoading(true);
      const response = await sellerApi.post("verifyResetOtp", {
        email: trimmedEmail,
        otp: trimmedOtp,
      });
      const responseData = response?.data;

      if (responseData?.success === false) {
        showError(responseData?.message || "OTP verification failed");
        return;
      }

      showSuccess(responseData?.message || "OTP verified successfully");
      setStep(3);
    } catch (err) {
      showError(getErrorMessage(err, "OTP verification failed"));
    } finally {
      setLoading(false);
    }
  }, [modalEmail, modalOtp]);

  const handleResetPassword = useCallback(async () => {
    const trimmedEmail = (emailRef.current || modalEmail).trim();
    const trimmedOtp = (otpRef.current || modalOtp).trim();
    const trimmedNewPassword = (newPasswordRef.current || "").trim();

    if (!trimmedEmail) {
      showError("Please enter your email address");
      return;
    }

    if (!trimmedOtp) {
      showError("Please enter the OTP");
      return;
    }

    if (!trimmedNewPassword) {
      showError("Please enter your new password");
      return;
    }

    try {
      setLoading(true);
      const response = await sellerApi.post("resetPassword", {
        email: trimmedEmail,
        otp: trimmedOtp,
        newPassword: trimmedNewPassword,
      });
      const responseData = response?.data;

      if (responseData?.success === false) {
        showError(responseData?.message || "Failed to reset password");
        return;
      }

      showSuccess(responseData?.message || "Password reset successfully");
      resetForgotPasswordFlow();
    } catch (err) {
      showError(getErrorMessage(err, "Failed to reset password"));
    } finally {
      setLoading(false);
    }
  }, [modalEmail, modalOtp, resetForgotPasswordFlow]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const emailValue = loginEmailRef.current;
    const passwordValue = passwordRef.current;
    
    try {
      const response = await sellerApi.post("sellerLogin", {
        email: emailValue,
        password: passwordValue,
      });
      const payload = response?.data;

      if (payload?.token && Number(payload?.user?.role_id) === 2) {
        loginSeller(payload);
        showSuccess(payload.message || "Login successful");
        navigate("/dashboard", { replace: true });
      } else {
        showError(payload?.message || "Seller authentication required");
      }
    } catch (err) {
      console.error("Login error:", err);
      showError(err.response?.data?.message || "Login failed");
    }
  }, [loginSeller, navigate]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  return (
    <>
      <div className="logopage">
        <a
          href="/sellerhome"
          className="seller-home-logo-link"
          aria-label="Elumia Med Store seller home"
        >
          <img
            src={mainlogo}
            alt="Elumia Med Store"
            className="seller-home-logo-login"
          />
        </a>
      </div>

      <div className="sb-login-page">
        <div className="sb-login-wrap">
          <div className="sb-left">
            <h2 className="sb-heading">Login</h2>

            <form className="sb-form" onSubmit={handleSubmit} noValidate>
              <label className="sb-label" htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                className="sb-input"
                type="email"
                placeholder="Enter your email"
                defaultValue={loginEmailRef.current}
                onChange={handleLoginEmailChange}
                required
                autoComplete="email"
              />

              <label className="sb-label" htmlFor="password">
                Password
              </label>
              <div className="sb-password-row">
                <input
                  id="password"
                  className="sb-input sb-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  defaultValue={passwordRef.current}
                  onChange={handlePasswordChange}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="sb-eye"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={togglePasswordVisibility}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                </button>
              </div>

              <div className="sb-row-between">
                <a
                  className="sb-forgot"
                  href="#forgot"
                  onClick={openForgotPasswordModal}
                >
                  Forgot Password?
                </a>
              </div>

              <button type="submit" className="sb-continue">
                Continue
              </button>
            </form>
          </div>

          <div className="sb-right">
            <img
              src={RIGHT_IMAGE}
              alt="Seller Login Background"
              className="sb-right-image"
              height={850}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={showForgotModal}
        onClose={resetForgotPasswordFlow}
        maxWidth="520px"
        ariaLabel="Forgot password"
      >
        <div className="modal-shell modal-shell--compact">
          <div className="modal-header">
            <p className="modal-kicker">Password Reset</p>
            <h2 className="modal-title">Forgot Password</h2>
            <p className="modal-subtitle">
              {step === 1 &&
                "Enter your email address to receive a reset OTP."}
              {step === 2 &&
                "Enter the OTP sent to your email address to continue."}
              {step === 3 &&
                "Set a new password for your seller account."}
            </p>
          </div>

          <div className="modal-body">
            {step === 1 && (
              <div>
                <label
                  className="sb-label"
                  htmlFor="forgot-password-email"
                  style={{ display: "block" }}
                >
                  Email Address
                </label>
                <input
                  id="forgot-password-email"
                  className="sb-input"
                  type="email"
                  placeholder="Enter your email"
                  value={modalEmail}
                  onChange={handleModalEmailChange}
                  autoComplete="email"
                />
              </div>
            )}

            {step === 2 && (
              <>
                <div>
                  <label
                    className="sb-label"
                    htmlFor="forgot-password-email-readonly"
                    style={{ display: "block" }}
                  >
                    Email Address
                  </label>
                  <input
                    id="forgot-password-email-readonly"
                    className="sb-input"
                    type="email"
                    value={modalEmail}
                    disabled
                    readOnly
                  />
                </div>

                <div>
                  <label
                    className="sb-label"
                    htmlFor="forgot-password-otp"
                    style={{ display: "block", marginTop: "12px" }}
                  >
                    OTP
                  </label>
                  <input
                    id="forgot-password-otp"
                    className="sb-input"
                    type="text"
                    placeholder="Enter OTP"
                    value={modalOtp}
                    onChange={handleOtpChange}
                    autoComplete="one-time-code"
                  />
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div>
                  <label
                    className="sb-label"
                    htmlFor="reset-password-email-readonly"
                    style={{ display: "block" }}
                  >
                    Email Address
                  </label>
                  <input
                    id="reset-password-email-readonly"
                    className="sb-input"
                    type="email"
                    value={modalEmail}
                    disabled
                    readOnly
                  />
                </div>

                <div>
                  <label
                    className="sb-label"
                    htmlFor="reset-password-new"
                    style={{ display: "block", marginTop: "12px" }}
                  >
                    New Password
                  </label>
                  <input
                    id="reset-password-new"
                    className="sb-input"
                    type="password"
                    placeholder="Enter new password"
                    onChange={handleNewPasswordChange}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}
          </div>

          <div className="modal-actions">
            {step > 1 && (
              <button
                type="button"
                className="modal-button modal-button--secondary"
                onClick={() => setStep((currentStep) => currentStep - 1)}
                disabled={loading}
              >
                Back
              </button>
            )}

            <button
              type="button"
              className="modal-button modal-button--primary"
              onClick={
                step === 1
                  ? handleForgotPassword
                  : step === 2
                    ? handleVerifyOtp
                    : handleResetPassword
              }
              disabled={loading}
            >
              {loading
                ? step === 1
                  ? "Submitting..."
                  : step === 2
                    ? "Verifying..."
                    : "Resetting..."
                : step === 1
                  ? "Submit"
                  : step === 2
                    ? "Verify"
                    : "Submit"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

const mapStateToProps = () => ({});
const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(SellerLogin);
